import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map } from 'rxjs';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  AuthenticationService,
  NotificationCounterService,
} from '@shared/services';
import * as bootstrap from 'bootstrap';

@Component({
  selector: 'mlk-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, RouterLink, RouterLinkActive, NgClass],
})
export class MenuComponent {
  /** Injection of {@link Router}. */
  private readonly router = inject(Router);

  /** Injection of {@link NotificationCounterService}. */
  private readonly notificationCounterService = inject(
    NotificationCounterService
  );

  /** Injection of {@link AuthenticationService}. */
  private readonly authenticationService = inject(AuthenticationService);

  readonly isAuthenticated = this.authenticationService.isAuthenticated;

  readonly user$ = this.authenticationService.isAuthenticated$.pipe(
    filter(Boolean),
    map(() => this.authenticationService.getCurrentUser())
  );

  readonly nbrNotifications =
    this.notificationCounterService.notificationUnread;

  onLogout(): void {
    this.authenticationService.logout();
    this.router.navigateByUrl('/');
  }

  onProfile(): void {
    this.router.navigateByUrl('/mlk/profile');
  }

  /**
   * Ouvre / ferme le menu burger (appelé au clic sur l'icône).
   */
  toggleMenu(): void {
    const menu = document.getElementById('mlk-menu') as HTMLElement | null;

    if (!menu) {
      return;
    }

    const bsCollapse = new bootstrap.Collapse(menu, {
      toggle: false,
    });

    if (menu.classList.contains('show')) {
      bsCollapse.hide();
    } else {
      bsCollapse.show();
    }
  }

  /**
   * Ferme le menu burger si le collapse est ouvert.
   */
  closeMenu(): void {
    const menu = document.getElementById('mlk-menu') as HTMLElement | null;

    if (!menu) {
      return;
    }

    if (menu.classList.contains('show')) {
      const bsCollapse = new bootstrap.Collapse(menu, {
        toggle: false,
      });
      bsCollapse.hide();
    }
  }

  /**
   * Fermer le menu si on clique en dehors de la navbar + menu.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const menu = document.getElementById('mlk-menu') as HTMLElement | null;
    const navbar = document.querySelector('nav.navbar') as HTMLElement | null;

    if (!menu || !navbar || !target) {
      return;
    }

    // Si le menu n'est pas ouvert, ne rien faire.
    if (!menu.classList.contains('show')) {
      return;
    }

    // Si on clique dans la navbar (burger, liens, icônes...), on ne ferme pas.
    if (navbar.contains(target)) {
      return;
    }

    // Sinon : clic en dehors -> on ferme.
    this.closeMenu();
  }

  /**
   * Fermer le menu si le focus se déplace en dehors de la navbar.
   * (Cas clavier / accessibilité)
   */
  @HostListener('document:focusin', ['$event'])
  onDocumentFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement | null;
    const menu = document.getElementById('mlk-menu') as HTMLElement | null;
    const navbar = document.querySelector('nav.navbar') as HTMLElement | null;

    if (!menu || !navbar || !target) {
      return;
    }

    if (!menu.classList.contains('show')) {
      return;
    }

    // Si le focus reste dans la navbar, on ne ferme pas.
    if (navbar.contains(target)) {
      return;
    }

    // Focus en dehors -> on ferme.
    this.closeMenu();
  }
}
